
import { Timestamp } from "firebase/firestore";
import { isValid } from "date-fns";
import { Offer } from "@/types/offer";
import { SortableOption } from "@/types/fieldsOptions";
import { ensureValidDate } from "@/utils/dateUtils";

/** Shared: enhance offers with availabilityStatusOption and signupStatusOption from options map. */
export function enhanceOffersWithOptions(
  offersList: Offer[],
  optionsMap: Record<string, SortableOption>
): Offer[] {
  return offersList.map((offer) => {
    const availabilityOption = optionsMap[offer.availabilityStatus];
    const signupOption = optionsMap[offer.signupStatus];
    return {
      ...offer,
      availabilityStatusOption: availabilityOption,
      signupStatusOption: signupOption,
    };
  });
}

export type TableDateFilter = "future" | "past" | "all" | "placeholder";

/** Shared: filter offers for table view by placeholder and date. */
export function filterOffersForTableView(
  offers: Offer[],
  tableDateFilter: TableDateFilter
): Offer[] {
  let filtered = [...offers];
  if (tableDateFilter === "placeholder") {
    filtered = filtered.filter((o) => !!o.isPlaceholder);
  } else {
    filtered = filtered.filter((o) => !o.isPlaceholder);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (tableDateFilter === "future") {
    filtered = filtered.filter((o) => {
      const start = ensureValidDate(o.startDate);
      const end = ensureValidDate(o.endDate);
      return start >= today || end >= today;
    });
  } else if (tableDateFilter === "past") {
    filtered = filtered.filter((o) => ensureValidDate(o.endDate) < today);
  }
  return filtered;
}

// Convert an offer to a session event for calendar display
export const offerToSessionEvent = (offer: Offer, programIndex?: number) => {
  // Safely convert dates with validation
  let startDate, endDate;
  
  try {
    if (offer.startDate instanceof Timestamp) {
      startDate = offer.startDate.toDate();
    } else if (offer.startDate instanceof Date) {
      startDate = isValid(offer.startDate) ? offer.startDate : new Date();
    } else {
      startDate = new Date(offer.startDate as any);
    }
    
    if (!isValid(startDate)) {
      console.warn(`Invalid startDate in offer ${offer.id}:`, offer.startDate);
      startDate = new Date();
    }
    
    if (offer.endDate instanceof Timestamp) {
      endDate = offer.endDate.toDate();
    } else if (offer.endDate instanceof Date) {
      endDate = isValid(offer.endDate) ? offer.endDate : new Date();
    } else {
      endDate = new Date(offer.endDate as any);
    }
    
    if (!isValid(endDate)) {
      console.warn(`Invalid endDate in offer ${offer.id}:`, offer.endDate);
      endDate = new Date();
    }
  } catch (error) {
    console.error(`Error processing dates for offer ${offer.id}:`, error);
    startDate = new Date();
    endDate = new Date();
  }
  
  // Use index-based color assignment if programIndex is provided
  let colorScheme = "blue";  // Default fallback
  
  if (programIndex !== undefined && programIndex >= 0) {
    // Use program index for consistent color assignment
    const colors = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'red', 'yellow', 'indigo', 'cyan'];
    colorScheme = colors[programIndex % colors.length];
  } else if (offer.variationKey) {
    // Fallback to deterministic approach based on variationKey
    const colors = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'red', 'yellow', 'indigo', 'cyan'];
    const hashIndex = offer.variationKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    colorScheme = colors[hashIndex];
  }
  
  // Extract regularProgrammingStart from variationData.timings if available
  const regularProgrammingStart = offer.variationData?.timings?.regularProgrammingStart || 0;

  return {
    id: offer.id,
    title: offer.displayTitle,
    startDate: startDate,
    endDate: endDate,
    variationKey: offer.variationKey,
    availabilityStatus: offer.availabilityStatus,
    signupStatus: offer.signupStatus,
    allDay: true,
    colorScheme: colorScheme,
    ageGroupId: offer.variationKey,
    // Add the numDaysCalc and regularProgrammingStart for sorting
    numDaysCalc: offer.numDaysCalc,
    regularProgrammingStart: regularProgrammingStart
  };
};
